import React, { useCallback, useEffect, useState, RefObject } from 'react';

import { CropSelectionOutline } from './CropSelectionOutline';
import { getCropPositions } from './utils';
import ContentDialog from '@/Pages/Sessions/uiComponents/ContentDialog/ContentDialog';
import Button from '@/components/atoms/Button/Button';
import { Scene, VideoCropPosition } from '@/player-config/src/types/RemotionPlayerConfigTypes/Scene';
import { updateClipData } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtils';
import { CLIP_UPDATE_TYPE } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtils/constants';
import useAnalytics from '@/hooks/useAnalytics';

interface VideoCropContainerProps {
  imageUrl: string;
  scene: Scene;
  clipId: string;
  selectedVideoId: string;
  isOpen: boolean;
  onClose: () => void;
  containerAspectRatio: number;
  videoAspectRatio: number;
  maintainAspectRatio?: boolean;
  cropInformation?: VideoCropPosition;
  compositeSegmentId: string;
  videoElementRef?: RefObject<HTMLDivElement>;
}

const clamp01 = (value: number): number => Math.max(0, Math.min(1, value));

const convertFromPreviewContainerToVideoFrame = ({
  coordinates,
  parentContainerAspectRatio,
  videoAspectRatio
}: {
  coordinates: VideoCropPosition;
  parentContainerAspectRatio: number;
  videoAspectRatio: number;
}): VideoCropPosition => {
  // Mirror the "contain" geometry used in `cropUtils.convertToParentContainerAspectRatio`, but invert it.
  // Normalize the parent container so height=1 and width=aspectRatio.
  const parentW = parentContainerAspectRatio;
  const parentH = 1;

  const isFitByWidth = videoAspectRatio >= parentContainerAspectRatio;
  const videoW = isFitByWidth ? parentW : parentH * videoAspectRatio;
  const videoH = videoW / videoAspectRatio;

  const xOffset = (parentW - videoW) / 2;
  const yOffset = (parentH - videoH) / 2;

  const toVideo = (p: { x: number; y: number }) => ({
    x: clamp01((p.x * parentW - xOffset) / videoW),
    y: clamp01((p.y * parentH - yOffset) / videoH)
  });

  return {
    top_left: toVideo(coordinates.top_left),
    bottom_right: toVideo(coordinates.bottom_right)
  };
};

const getCropBoxFromPreviewTransform = (
  previewElement: HTMLVideoElement | HTMLCanvasElement
): VideoCropPosition | null => {
  const transform = previewElement.style.transform || globalThis.getComputedStyle(previewElement).transform;
  if (!transform || transform === 'none') {
    return null;
  }

  // IMPORTANT: This regex only matches the specific format: translate(<x>%, <y>%) scale(<s>)
  // Browsers often normalize transforms to matrix() when reading getComputedStyle.
  // This function relies on the inline style.transform being set in our expected format.
  // If getComputedStyle returns a matrix() string, the regex will fail and return null.
  const translateScaleMatch = /translate\(\s*([-\d.]+)%\s*,\s*([-\d.]+)%\s*\)\s*scale\(\s*([-\d.]+)\s*\)/.exec(
    transform
  );
  if (!translateScaleMatch) {
    return null;
  }

  const translateXPercent = Number(translateScaleMatch[1]);
  const translateYPercent = Number(translateScaleMatch[2]);
  const scale = Number(translateScaleMatch[3]);
  if (!Number.isFinite(scale) || scale <= 0) {
    return null;
  }

  // transform is: translate(-(center-0.5)*scale*100%, ...) scale(scale)
  const centerX = 0.5 - translateXPercent / (scale * 100);
  const centerY = 0.5 - translateYPercent / (scale * 100);

  // ASSUMPTION: We only support uniform scale(s), not scale(sx, sy).
  // If non-uniform scale is applied, this calculation will be incorrect.
  const cropWidth = 1 / scale;
  const cropHeight = 1 / scale;

  return {
    top_left: { x: clamp01(centerX - cropWidth / 2), y: clamp01(centerY - cropHeight / 2) },
    bottom_right: { x: clamp01(centerX + cropWidth / 2), y: clamp01(centerY + cropHeight / 2) }
  };
};

export default function VideoCropContainer({
  imageUrl,
  compositeSegmentId,
  scene,
  clipId,
  selectedVideoId,
  isOpen,
  onClose,
  maintainAspectRatio = true,
  containerAspectRatio,
  cropInformation,
  videoAspectRatio,
  videoElementRef
}: Readonly<VideoCropContainerProps>) {
  const [effectiveContainerAspectRatio, setEffectiveContainerAspectRatio] = useState(containerAspectRatio);
  const [cropPositions, setCropPositions] = useState(
    getCropPositions(scene, selectedVideoId, containerAspectRatio, videoAspectRatio)
  );

  const setIsOpen = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        onClose();
      }
    },
    [onClose]
  );

  const updateCropPositions = useCallback(
    (newPositions: { left: number; top: number; width: number; height: number }) => {
      setCropPositions(newPositions);
    },
    []
  );

  const { trackManualRepositioningApplied } = useAnalytics();

  const handleCropComplete = useCallback(() => {
    const newCropPositions = {
      top_left: {
        x: cropPositions.left,
        y: cropPositions.top
      },
      bottom_right: {
        x: cropPositions.left + cropPositions.width,
        y: cropPositions.top + cropPositions.height
      }
    };
    trackManualRepositioningApplied({ clipId, fromRemotionPlayer: true });
    updateClipData({
      clipId,
      actionType: CLIP_UPDATE_TYPE.UPDATE_SCENE_CROP_POSITION,
      compositeId: compositeSegmentId,
      cropPosition: newCropPositions,
      sceneId: scene.id,
      videoId: selectedVideoId
    });
    onClose();
  }, [
    clipId,
    compositeSegmentId,
    cropPositions.height,
    cropPositions.left,
    cropPositions.top,
    cropPositions.width,
    onClose,
    scene.id,
    selectedVideoId,
    trackManualRepositioningApplied
  ]);

  useEffect(() => {
    if (isOpen) {
      const container = videoElementRef?.current;
      const rect = container?.getBoundingClientRect();
      const liveAspectRatioFromPreview =
        rect && rect.width > 0 && rect.height > 0 ? rect.width / rect.height : undefined;

      const nextAspectRatio = liveAspectRatioFromPreview ?? containerAspectRatio;
      setEffectiveContainerAspectRatio(nextAspectRatio);

      const defaultPositions = getCropPositions(scene, selectedVideoId, nextAspectRatio, videoAspectRatio);

      const previewElement = container
        ? (container.querySelector<HTMLVideoElement>('video') ?? container.querySelector<HTMLCanvasElement>('canvas'))
        : null;
      const cropFromLivePreview = previewElement ? getCropBoxFromPreviewTransform(previewElement) : null;
      const cropForModal = cropFromLivePreview
        ? convertFromPreviewContainerToVideoFrame({
            coordinates: cropFromLivePreview,
            parentContainerAspectRatio: nextAspectRatio,
            videoAspectRatio
          })
        : cropInformation;

      setCropPositions(
        cropForModal
          ? {
              left: cropForModal.top_left.x,
              top: cropForModal.top_left.y,
              width: cropForModal.bottom_right.x - cropForModal.top_left.x,
              height: cropForModal.bottom_right.y - cropForModal.top_left.y
            }
          : defaultPositions
      );
    }
  }, [isOpen, scene, selectedVideoId, containerAspectRatio, cropInformation, videoAspectRatio, videoElementRef]);

  return (
    <div onPointerDown={e => e.stopPropagation()}>
      <ContentDialog
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        title={'Crop Video'}
        size="large"
        panelClassNames="!max-w-4xl"
      >
        <div className="flex flex-col p-6">
          <div className="relative mb-6 flex items-center justify-center overflow-hidden">
            <div className="relative overflow-hidden">
              <img
                src={imageUrl}
                alt="Cropped video preview"
                className="size-full max-h-[calc(75vh-172px)] w-auto max-w-full object-contain"
                data-testid="crop-preview-image"
              />

              {/* Crop Selection Outline */}
              <div className="absolute inset-0">
                <CropSelectionOutline
                  positions={cropPositions}
                  aspectRatio={effectiveContainerAspectRatio}
                  videoAspectRatio={videoAspectRatio}
                  updatePositions={updateCropPositions}
                  maintainAspectRatio={maintainAspectRatio}
                />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end border-t border-slate-200 pt-4">
            <div className="flex items-center space-x-3">
              <Button variation="outline" onClick={onClose} trackingId="video-crop-cancel-button">
                Cancel
              </Button>
              <Button variation="filled" onClick={handleCropComplete} trackingId="video-crop-save-button">
                Apply Crop
              </Button>
            </div>
          </div>
        </div>
      </ContentDialog>
    </div>
  );
}
